import { useTranslation } from 'react-i18next';
import { Cpu } from 'lucide-react';

import { EmptyState, SectionCard } from '../../../components/design-system/admin';
import { PROVIDER_NAMES, type ProviderView } from '../../../common/models/platform-costs.model';
import { money, moneyShort } from './cost-format';

/** Hours in an average month. */
const MONTH_HOURS = 730;

/** Every GPU billed by the hour right now, across providers, and what it costs if nobody stops it. */
export function RunningNowCard({ providers, className }: { providers: ProviderView[]; className?: string }) {
  const { t } = useTranslation();
  const items = providers.flatMap((p) => (p.running || []).map((r) => ({ ...r, provider: p.provider }))).sort((a, b) => b.usdPerHour - a.usdPerHour);
  const perHour = items.reduce((s, r) => s + r.usdPerHour, 0);

  return (
    <SectionCard
      title={t('ADMIN_COSTS.RUNNING.TITLE')}
      description={t('ADMIN_COSTS.RUNNING.DESCRIPTION')}
      action={items.length ? <span className="text-sm font-medium tabular-nums">{money(perHour)}/h</span> : undefined}
      className={className}
      testId="costs-running"
    >
      {!items.length ? (
        <EmptyState icon={<Cpu />} title={t('ADMIN_COSTS.RUNNING.NONE')} className="py-8" />
      ) : (
        <>
          <ul className="flex flex-col divide-y">
            {items.map((r) => (
              <li key={`${r.provider}:${r.key}`} className="flex items-center justify-between gap-3 py-2.5 first:pt-0">
                <span className="flex min-w-0 items-center gap-2.5">
                  <span className="relative flex size-2 shrink-0" aria-hidden>
                    <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-60 motion-reduce:animate-none" />
                    <span className="relative inline-flex size-2 rounded-full bg-success" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm">{r.label}</span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      {PROVIDER_NAMES[r.provider]}
                      {r.detail ? ` · ${r.detail}` : ''}
                    </span>
                  </span>
                </span>
                <span className="shrink-0 text-right text-sm font-medium tabular-nums">{money(r.usdPerHour)}/h</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-muted-foreground">{t('ADMIN_COSTS.RUNNING.MONTHLY', { amount: moneyShort(perHour * MONTH_HOURS) })}</p>
        </>
      )}
    </SectionCard>
  );
}
